import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/patterns";
import { ErrorState } from "@/components/patterns/States";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Rule = { id: string; rule_key: string; rule_name: string; value_json: any };

const WHO = [
  { role: "Sales Executives", approver: "their Sales Head", quote: "QUOTE_REVIEW_SALES_EXEC", proposal: "PROPOSAL_REVIEW_SALES_EXEC", agreement: "AGREEMENT_REVIEW_SALES_EXEC" },
  { role: "Sales Heads", approver: "a Super Admin", quote: "QUOTE_REVIEW_SALES_HEAD", proposal: "PROPOSAL_REVIEW_SALES_HEAD", agreement: "AGREEMENT_REVIEW_SALES_HEAD" },
] as const;

function Card({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-[10px] border border-border bg-card px-[18px] py-4">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
      {children}
    </section>
  );
}

/** Settings → Approvals: who has to have their quotes and proposals approved before they go to the customer, and how fast the approver is chased */
export function ApprovalsSettingsPage() {
  const { success, error } = useToast();
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [hours, setHours] = useState({ remind: "4", escalate: "24" });
  const [followup, setFollowup] = useState("3");

  const load = useCallback(async () => {
    try {
      setFailed(false);
      const list = unwrapArray<Rule>(await api.get("/api/SettingRule/List"));
      setRules(list);
      const num = (k: string, d: string) => String(list.find((r) => r.rule_key === k)?.value_json ?? d);
      setHours({ remind: num("APPROVAL_REMINDER_HOURS", "4"), escalate: num("APPROVAL_ESCALATION_HOURS", "24") });
      setFollowup(num("AGREEMENT_FOLLOWUP_DAYS", "3"));
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const rule = (key: string) => rules?.find((r) => r.rule_key === key);
  const save = async (key: string, value: boolean | number) => {
    const r = rule(key);
    if (!r) return error("Not available", "This setting isn't in the database yet; the latest update needs to finish first");
    try {
      await api.put(`/api/SettingRule/Update/${r.id}`, { rule_key: r.rule_key, rule_name: r.rule_name, value_json: value });
      setRules((rs) => (rs || []).map((x) => (x.id === r.id ? { ...x, value_json: value } : x)));
      success("Saved", "The change applies to what is submitted from now on");
    } catch (err: any) {
      error("Could not save", err?.message || "Please try again");
    }
  };
  const saveHours = async () => {
    const remind = Number(hours.remind);
    const escalate = Number(hours.escalate);
    if (!(remind >= 1) || !(escalate >= remind)) return error("Check the hours", "Remind after at least 1 hour, and pass it up no sooner than the reminder");
    await save("APPROVAL_REMINDER_HOURS", remind);
    await save("APPROVAL_ESCALATION_HOURS", escalate);
  };

  const saveFollowup = async () => {
    const days = Number(followup);
    if (!(days >= 1)) return error("Check the days", "Remind after at least 1 day");
    await save("AGREEMENT_FOLLOWUP_DAYS", days);
  };

  if (failed) return <ErrorState title="Couldn't load the approval settings" onRetry={load} />;
  if (!rules) return <FormSkeleton />;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <PageHeader title="Approvals" />
      <p className="text-sm text-muted-foreground">Choose whose quotes, proposals and agreements have to be approved before they can be sent to a customer. Nobody can approve their own, and anything changed after approval has to be approved again.</p>

      <Card title="Who needs approval" sub="Tick what has to wait for approval. The approver is the next person up the chain; a Super Admin approves when there is nobody above.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[460px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-1.5 font-semibold">Made by</th>
                <th className="w-[90px] text-center font-semibold">Quotes</th>
                <th className="w-[100px] text-center font-semibold">Proposals</th>
                <th className="w-[110px] text-center font-semibold">Agreements</th>
                <th className="py-1.5 pl-3 font-semibold">Approved by</th>
              </tr>
            </thead>
            <tbody>
              {WHO.map((w) => (
                <tr key={w.role} className="border-t border-border/60">
                  <td className="py-2.5 font-medium">{w.role}</td>
                  {([w.quote, w.proposal, w.agreement] as const).map((key, i) => (
                    <td key={key} className="text-center">
                      <input type="checkbox" className="size-4 accent-primary" checked={rule(key)?.value_json === true} disabled={!rule(key)} onChange={(e) => save(key, e.target.checked)} aria-label={`${w.role}' ${["quotes", "proposals", "agreements"][i]} need approval`} />
                    </td>
                  ))}
                  <td className="py-2.5 pl-3 text-muted-foreground">{w.approver}</td>
                </tr>
              ))}
              <tr className="border-t border-border/60">
                <td className="py-2.5 font-medium">Super Admins</td>
                <td className="text-center text-muted-foreground">—</td>
                <td className="text-center text-muted-foreground">—</td>
                <td className="text-center text-muted-foreground">—</td>
                <td className="py-2.5 pl-3 text-muted-foreground">Never need approval</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Quotes also wait for approval when the discount is above the person's limit or the margin is too low, whoever made them. Those limits are in <Link to="/settings/rules" className="text-primary hover:underline">Business rules</Link> and on each role.
        </p>
      </Card>

      <Card title="Unsigned agreements" sub="An agreement that has been sent and is still missing signatures reminds its owner, and again after the same number of days while it stays unsigned.">
        <div className="max-w-xs space-y-1.5">
          <label htmlFor="agr-followup" className="text-[12px] font-medium text-foreground/80">Remind the owner after (days)</label>
          <Input id="agr-followup" type="number" min={1} value={followup} onChange={(e) => setFollowup(e.target.value)} className="h-9 text-[13px]" />
        </div>
        <Button type="button" size="sm" onClick={saveFollowup}>Save days</Button>
      </Card>

      <Card title="Reminders" sub="How long something may wait before its approver is chased, and before it goes to the next person up.">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="apr-remind" className="text-[12px] font-medium text-foreground/80">Remind the approver after (hours)</label>
            <Input id="apr-remind" type="number" min={1} value={hours.remind} onChange={(e) => setHours((h) => ({ ...h, remind: e.target.value }))} className="h-9 text-[13px]" />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="apr-escalate" className="text-[12px] font-medium text-foreground/80">Pass it up after (hours)</label>
            <Input id="apr-escalate" type="number" min={1} value={hours.escalate} onChange={(e) => setHours((h) => ({ ...h, escalate: e.target.value }))} className="h-9 text-[13px]" />
          </div>
        </div>
        <Button type="button" size="sm" onClick={saveHours}>Save hours</Button>
      </Card>
    </div>
  );
}
